/**
 * The shell: masthead, language toggle, tabs, and the route.
 *
 * The magic link is completed here rather than on the sign-in screen, because
 * an applicant may open the link on any URL and the one-time parameters have to
 * be consumed before anything else runs.
 */

import { useEffect, useRef, useState } from "react";
import { bi } from "@ksidc/ontology";
import { ThothicaFooter, useLang, useSession } from "@ksidc/client";
import { LangToggle } from "./components/LangToggle.js";
import { ErrorNote, Loading } from "./components/ui.js";
import { Applications } from "./screens/Applications.js";
import { Apply } from "./screens/Apply.js";
import { Discover } from "./screens/Discover.js";
import { Letters } from "./screens/Letters.js";
import { Messages } from "./screens/Messages.js";
import { SignIn } from "./screens/SignIn.js";
import { Status } from "./screens/Status.js";
import { HelpScreen } from "./screens/help.js";
import { APPLY, DISCOVER, HELP, HOME, LETTERS, navigate, useRoute } from "./router.js";

type LinkPhase = "none" | "completing" | "need-email" | "failed";

function useMagicLink(): {
  phase: LinkPhase;
  error: string | null;
  finishWith: (email: string) => Promise<void>;
} {
  const { auth } = useSession();
  const [phase, setPhase] = useState<LinkPhase>("none");
  const [error, setError] = useState<string | null>(null);
  // A sign-in link works once, so it is consumed once however often this runs.
  const attempted = useRef(false);

  useEffect(() => {
    if (!auth) return;
    if (attempted.current) return;
    if (!auth.isMagicLink()) return;
    attempted.current = true;
    setPhase("completing");
    void (async () => {
      try {
        await auth.completeMagicLink();
        setPhase("none");
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        if (message.includes("NEED_EMAIL")) {
          setPhase("need-email");
        } else {
          setPhase("failed");
          setError(message);
        }
      }
    })();
  }, [auth]);

  const finishWith = async (email: string): Promise<void> => {
    if (!auth) return;
    setPhase("completing");
    try {
      await auth.completeMagicLink(email);
      setPhase("none");
    } catch (e) {
      setPhase("need-email");
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return { phase, error, finishWith };
}

export function App(): JSX.Element {
  const session = useSession();
  const { t, lang } = useLang();
  const route = useRoute();
  const link = useMagicLink();

  const signedIn = session.status === "signed-in";

  return (
    <div className="shell">
      <header className="masthead">
        <div className="masthead-inner">
          <p className="wordmark">
            <a href={HOME} style={{ color: "inherit", textDecoration: "none" }}>
              {t(bi("Single Window Clearance", "ഏകജാലക അനുമതി"))}
            </a>
            <small>{t(bi("Kerala Act 5 of 2000", "2000-ലെ കേരള നിയമം 5"))}</small>
          </p>
          <div className="masthead-tools">
            <LangToggle />
            {signedIn && session.auth ? (
              <button
                type="button"
                className="btn-quiet"
                onClick={() => void session.auth?.signOut()}
              >
                {t(bi("Sign out", "പുറത്തുകടക്കുക"))}
              </button>
            ) : null}
          </div>
        </div>
      </header>

      {signedIn ? (
        <nav className="tabs no-print" aria-label={t(bi("Sections", "വിഭാഗങ്ങൾ"))}>
          <div className="tabs-inner">
            <Tab
              href={DISCOVER}
              current={route.name === "discover"}
              label={bi("What do I need", "എനിക്ക് എന്ത് വേണം")}
            />
            <Tab href={APPLY} current={route.name === "apply"} label={bi("Apply", "അപേക്ഷിക്കുക")} />
            <Tab
              href={HOME}
              current={route.name === "home" || route.name === "status" || route.name === "messages"}
              label={bi("My applications", "എന്റെ അപേക്ഷകൾ")}
            />
            <Tab
              href={LETTERS}
              current={route.name === "letters"}
              label={bi("Letters", "കത്തുകൾ")}
            />
            <Tab
              href={HELP}
              current={route.name === "help"}
              label={bi("How to use this", "എങ്ങനെ ഉപയോഗിക്കാം")}
            />
          </div>
        </nav>
      ) : null}

      <main>
        <Body session={session} route={route} link={link} />
      </main>

      <footer className="colophon">
        <div className="colophon-inner">
        <p style={{ marginBottom: 0 }}>
          {t(
            bi(
              "Kerala Industrial Single Window Clearance Boards and Industrial Township Area Development Act, 1999, with the Rules of 2000 as amended in 2021.",
              "1999-ലെ കേരള വ്യവസായ ഏകജാലക അനുമതി ബോർഡുകളും വ്യവസായ ടൗൺഷിപ്പ് ഏരിയ വികസന നിയമവും, 2021-ൽ ഭേദഗതി ചെയ്ത 2000-ലെ ചട്ടങ്ങൾ സഹിതം.",
            ),
          )}
        </p>
        <ThothicaFooter lang={lang} />
        </div>
      </footer>
    </div>
  );
}

function Tab({
  href,
  current,
  label,
}: {
  href: string;
  current: boolean;
  label: { en: string; ml: string };
}): JSX.Element {
  const { t } = useLang();
  return (
    <button
      type="button"
      className="tab"
      aria-current={current ? "page" : undefined}
      onClick={() => navigate(href)}
    >
      {t(label)}
    </button>
  );
}

function Body({
  session,
  route,
  link,
}: {
  session: ReturnType<typeof useSession>;
  route: ReturnType<typeof useRoute>;
  link: ReturnType<typeof useMagicLink>;
}): JSX.Element {
  const { t } = useLang();

  if (link.phase === "completing") return <SignIn completing />;
  if (link.phase === "need-email") {
    return <SignIn needEmail onProvideEmail={link.finishWith} linkError={link.error} />;
  }

  if (session.status === "loading") {
    return <Loading what={bi("Starting up", "ആരംഭിക്കുന്നു")} />;
  }

  if (session.status === "error") {
    return (
      <div className="stack">
        <ErrorNote error={session.error ?? "unknown"} />
        <p className="muted">
          {t(
            bi(
              "The system could not read its own configuration, so nothing can be signed into yet.",
              "സംവിധാനത്തിന് സ്വന്തം ക്രമീകരണം വായിക്കാൻ കഴിഞ്ഞില്ല, അതിനാൽ ഇപ്പോൾ പ്രവേശിക്കാൻ കഴിയില്ല.",
            ),
          )}
        </p>
      </div>
    );
  }

  if (session.status !== "signed-in") {
    return <SignIn linkError={link.phase === "failed" ? link.error : null} />;
  }

  switch (route.name) {
    case "discover":
      return <Discover />;
    case "apply":
      return <Apply />;
    case "status":
      return <Status id={route.id} />;
    case "messages":
      return <Messages id={route.id} />;
    case "letters":
      return <Letters />;
    case "help":
      return <HelpScreen />;
    default:
      return <Applications />;
  }
}
